/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { FieldArray, Field, Form, Formik } from 'formik';
import { debounce } from "lodash";
import * as yup from 'yup';
import Icon from 'components/Icon';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  InlineLabel,
  ViewText,
  RadioContainer,
  CalendarContainer,
  ButtonContainer,
  ButtonContent,
  LabelEdit,
  YearMonthSelect,
  TableLineCheckbox,
} from './styles';
import { GridColumnTitle, Label } from 'components/Form';
import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import InputField from 'components/Form/InputField';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button from 'components/Button';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import { RadioButton } from "components/Form/Radio/index";
import { DayPicker } from 'react-day-picker';
import SearchField from 'components/Form/SearchField';
import MasksHelper from 'utils/src/masks';
import ModalConfirmDelete from 'components/ModalConfirmDelete';
import CheckBox from "components/Form/CheckBox";

const RelationshipInformation = ({ idPerson, columns, columnsSelect, data, personInsurers, hasPermission, onSavePersonRelationship }) => {
  const formikRef = useRef(null);
  const inputRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const maskHelper = new MasksHelper();

  const [sortBy, setSortBy] = useState({ key: null, direction: null, });
  const [addPersonRelationship, setAddPersonRelationship] = useState(false);
  const [idPersonRelationship, setIdPersonRelationship] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [searchList, setSearchList] = useState([])
  const [validations, setValidations] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addNewMarket, setAddNewMarket] = useState(false);
  const [insurerPersonList, setInsurerPersonList] = useState([]);
  const [insurerPersonSelected, setInsurerPersonSelected] = useState([]);
  const [addAllSelectNewMarket, setAddAllSelectNewMarket] = useState(false);

  const [initial, setInitial] = useState({
    ndaStatus: '',
    capacityExposure: '',
    ndaExpireDate: '',
    personTailLastName: '',
    idPersonTail: '',
  });


  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const fromMonth = new Date(currentYear, currentMonth);
  const toMonth = new Date(currentYear + 50, 11);
  const [month, setMonth] = useState(fromMonth);

  const YearMonthForm = ({ date, localeUtils, onChange }) => {
    const months = localeUtils.getMonths();

    const years = [];
    for (let i = fromMonth.getFullYear() - 10; i <= toMonth.getFullYear(); i += 1) {
      years.push(i);
    }

    const handleChange = (e) => {
      const { year, month } = e.target.form;
      onChange(new Date(year.value, month.value));
    };

    return (
      <form className="DayPicker-Caption">
        <YearMonthSelect name="month" onChange={handleChange} value={date.getMonth()}>
          {months.map((month, i) => (
            <option key={month} value={i}>
              {month}
            </option>
          ))}
        </YearMonthSelect>
        <YearMonthSelect name="year" onChange={handleChange} value={date.getFullYear()}>
          {years.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </YearMonthSelect>
      </form>
    );
  }

  const handleYearMonthChange = (month) => {
    setMonth(month);
  }

  const setEditPersonRelationship = (personRelationship) => {
    formikRef.current.values.ndaStatus = personRelationship.relationshipContent.ndaStatus;
    formikRef.current.values.capacityExposure = personRelationship.relationshipContent.capacityExposure;
    formikRef.current.values.ndaExpireDate = personRelationship.relationshipContent.ndaExpireDate;
    formikRef.current.values.personTailLastName = personRelationship.personTailLastName;
    formikRef.current.values.idPersonTail = personRelationship.idPersonTail;

    setAddPersonRelationship(true);
    setIdPersonRelationship(personRelationship.idPersonRelationship);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 100)
  }

  const resetForm = () => {
    formikRef.current.values.ndaStatus = '';
    formikRef.current.values.capacityExposure = '';
    formikRef.current.values.ndaExpireDate = '';
    formikRef.current.values.personTailLastName = '';
    formikRef.current.values.idPersonTail = '';

    insurerPersonList.map(item => {
      item.isSelected = false
    })
    setInsurerPersonList(JSON.parse(JSON.stringify(insurerPersonList)))
    setInsurerPersonSelected([])
  }

  const saveRelationship = async (body, isBulkUpdate) => {
    try {
      loading.start();
      body.idPersonHead = idPerson;
      body.idPersonRelationship = idPersonRelationship;
      body.idDomain = 314;
      body.relationshipContent = {
        ndaStatus: body.ndaStatus,
        capacityExposure: body.capacityExposure,
        ndaExpireDate: body.ndaExpireDate
      }

      if (!body.idPersonTail && isBulkUpdate) return;

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSavePersonRelationship(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      if (!isBulkUpdate) {
        setIdPersonRelationship(null);
        setAddPersonRelationship(false);
        resetForm();
        onSavePersonRelationship && onSavePersonRelationship();
      }
    } catch (err) {
      if (!isBulkUpdate) {
        loading.stop();
        mc.error("An error happened, please try again.");
      }
    };
  }
  const saveAllInsurersSelected = async (listSelecteds) => {
    for (const selected of listSelecteds) {
      if (!selected.idPerson) continue;

      try {
        selected.idPersonHead = idPerson;
        selected.personTailLastName = 314;
        selected.idPersonTail = selected.idPerson;
        selected.capacityExposure = "0.00";
        selected.ndaExpireDate = " 31/12/2999";
        selected.ndaStatus = true;
        if (selected.idPersonTail) await saveRelationship(selected, true)
      } catch (e) {
      }
    }
    loadInsurers();
    setAddNewMarket(false);
    setIdPersonRelationship(null);
    setAddPersonRelationship(false);
    resetForm();
    onSavePersonRelationship && onSavePersonRelationship();
  }

  const deletePersonRelationship = async (idPersonRelationship) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestDeletePersonRelationship(idPersonRelationship).then(res => res.json());
      setIdPersonRelationship(null);

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      onSavePersonRelationship && onSavePersonRelationship();
    } catch (err) {
      setIdPersonRelationship(null);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const searchCompanies = async (searchValue) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestGetPersonsList(`?fullName=${searchValue}&personType=35`).then((response) => response.json()).then((data) => {
        if (data.ret_code != 200) {
          return;
        }

        data.ret_value = data.ret_value?.map(v => ({ ...v, id: v.idPerson, personName: `${v.lastName}` }))
        setSearchList(data.ret_value);
      });
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
    }
  }

  const createValidation = () => {
    let validations = yup.object().shape({
      ndaStatus: yup.string().required("* Required field"),
      capacityExposure: yup.string().required("* Required field"),
      ndaExpireDate: yup.string().notRequired(),
      idPersonTail: yup.string().required("* Required field"),
    });
    setValidations(validations);
    return validations
  }

  const addInsurerPerson = (item) => {
    loading.start();
    let listAux = insurerPersonSelected;
    listAux.push(item);
    setInsurerPersonSelected(listAux);

    insurerPersonList.map(insurer => {
      if (insurer.idPerson == item.idPerson) {
        item.isSelected = true;
      }
    })
    setInsurerPersonList(JSON.parse(JSON.stringify(insurerPersonList)))
    loading.stop();
  }

  const removeInsurerPerson = (item) => {
    loading.start();
    let listFound = insurerPersonSelected.filter((insurer, index) => insurer.idPerson != item.idPerson);

    setInsurerPersonSelected(listFound);

    insurerPersonList.map(insurer => {
      if (insurer.idPerson == item.idPerson) {
        item.isSelected = false;
      }
    })
    setInsurerPersonList(JSON.parse(JSON.stringify(insurerPersonList)))
    loading.stop();
  }

  const handleAllSelectedInsurers = () => {
    setAddAllSelectNewMarket(!addAllSelectNewMarket);
    return !addAllSelectNewMarket;
  }

  const addAllInsurerPerson = () => {
    loading.start();

    insurerPersonList.map(insurer => {
      insurer.isSelected = true;
    })
    setInsurerPersonList(JSON.parse(JSON.stringify(insurerPersonList)))
    setInsurerPersonSelected(JSON.parse(JSON.stringify(insurerPersonList)))
    loading.stop();
  }

  const removeAllInsurerPerson = () => {
    loading.start();
    insurerPersonList.map(insurer => {
      insurer.isSelected = false;
    })
    setInsurerPersonList(JSON.parse(JSON.stringify(insurerPersonList)))
    setInsurerPersonSelected([]);
    loading.stop();
  }

  const loadInsurers = () => {
    let listAux = personInsurers;
    data.map(item => {
      listAux = listAux.filter(insurer => insurer.idPerson != item.idPersonTail)
    })
    insurerPersonSelected.map(item => {
      listAux = listAux.filter(insurer => insurer.idPerson != item.idPerson)
    })
    setInsurerPersonList(JSON.parse(JSON.stringify(listAux)))
  }

  useEffect(() => {
    createValidation();
    loadInsurers();
  }, []);

  useEffect(() => {
    loadInsurers();
  }, [personInsurers, addNewMarket]);

  const debouncedChangeHandler = useCallback(
    debounce(searchCompanies, 300)
    , []);

  const sortListAlphabetically = (list) => {
    return list.sort((a, b) => {
      const nameA = a.personTailLastName ? a.personTailLastName?.toLowerCase() : a.personLastName?.toLowerCase();
      const nameB = b.personTailLastName ? b.personTailLastName?.toLowerCase() : b.personLastName?.toLowerCase();
      
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => saveRelationship(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete show={deleteModalOpen} onModalClose={() => { setDeleteModalOpen(false); setIdPersonRelationship(null); }} onConfirm={() => deletePersonRelationship(idPersonRelationship)} />
          {
            addNewMarket ?
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      {columnsSelect.map((column, index) =>
                        <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                          <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                            {column.label}
                          </GridColumnTitle>
                          {column.value && sortBy.key === column.value && sortBy.direction === "asc" ? <Icon name={"arrowup"} /> :
                            column.value && sortBy.key === column.value && sortBy.direction === "desc" ? <Icon name={"arrowdown"} /> : ''}
                        </TableHeader>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow key={'index'} >
                      <TableCollum>
                        <InlineLabel>
                          Main
                        </InlineLabel>
                        <label>
                          <CheckBox
                            name={'Select All'}
                            type="checkbox"
                            checked={addAllSelectNewMarket}
                            onChange={() => {
                              let allSelected = handleAllSelectedInsurers()
                              setAddAllSelectNewMarket(allSelected)
                              if (allSelected) {
                                addAllInsurerPerson()
                              } else {
                                removeAllInsurerPerson()
                              }
                            }
                            }
                          />
                        </label>

                      </TableCollum>
                      <TableCollum>
                        <InlineLabel>
                          Insurer
                        </InlineLabel>
                        <LabelEdit fontSize={12}>
                          Select All
                        </LabelEdit>
                      </TableCollum>
                    </TableRow>
                    <FieldArray
                      name="management"
                      render={() => sortListAlphabetically(insurerPersonList).map((item, index) => {
                        if (item.personLastName) {
                          return (
                            <TableRow key={index} >
                              <TableCollum>
                                <InlineLabel>
                                  Main
                                </InlineLabel>
                                <label>
                                  <CheckBox
                                    name={item.idPerson}
                                    type="checkbox"
                                    checked={item.isSelected}
                                    onChange={(e) => {
                                      if (item.isSelected) {
                                        item.isSelected = false
                                        removeInsurerPerson(item)
                                      } else {
                                        item.isSelected = true
                                        addInsurerPerson(item)
                                      }
                                    }
                                    }
                                  />
                                </label>
                              </TableCollum>

                              <TableCollum>
                                <InlineLabel>
                                  Insurer
                                </InlineLabel>
                                <LabelEdit fontSize={12}>
                                  {item.personLastName}
                                </LabelEdit>
                              </TableCollum>

                              <TableCollum>
                                <InlineLabel>
                                  Capacity Exposure
                                </InlineLabel>
                                <LabelEdit fontSize={12}>
                                  0
                                </LabelEdit>
                              </TableCollum>

                              <TableCollum>
                                <InlineLabel>
                                  NDA
                                </InlineLabel>
                                <LabelEdit fontSize={12}>
                                  YES
                                </LabelEdit>
                              </TableCollum>

                              <TableCollum>
                                <InlineLabel>
                                  NDA Expiration Date
                                </InlineLabel>
                                <LabelEdit fontSize={12}>
                                  31/12/2999
                                </LabelEdit>
                              </TableCollum>

                            </TableRow>
                          )
                        }
                      })
                      }
                    />
                  </TableBody>
                </Table>
                <ButtonContainer>
                  <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                  <ButtonContent>
                    <Button
                      fullWidth
                      style={{ marginLeft: 15 }}
                      third
                      onClick={() => {
                        setAddNewMarket(false)
                        setIdPersonRelationship(null);
                        setAddPersonRelationship(false);
                        resetForm();
                      }}
                    >
                      CANCEL
                    </Button>
                  </ButtonContent>
                  <ButtonContent>
                    <Button
                      type="button"
                      fullWidth
                      style={{ marginLeft: 15 }}
                      primary
                      onClick={() => {
                        saveAllInsurersSelected(insurerPersonSelected)
                      }}
                    >
                      ADD
                    </Button>
                  </ButtonContent>
                </ButtonContainer>
              </>
              :
              <Table>
                <TableHead>
                  <TableRow>
                    {columns.map((column, index) =>
                      <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                        <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                          {column.label}
                        </GridColumnTitle>
                        {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                          <Icon name={"arrowup"} /> :
                          column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                            <Icon name={"arrowdown"} /> :
                            ''}
                      </TableHeader>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <FieldArray
                    name="management"
                    render={() => sortListAlphabetically(data).map((item, index) => (
                      <TableRow key={index} >
                        <TableCollum>
                          <InlineLabel>
                            Insurer
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.personTailLastName}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Capacity Exposure
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.relationshipContent.capacityExposure}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            NDA
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.relationshipContent.ndaStatus ? 'YES' : "NO"}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            NDA Expiration Date
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.relationshipContent.ndaExpireDate}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum right>
                          <InlineLabel>
                            ACTION
                          </InlineLabel>
                          <Label>
                            <Icon
                              name="edit"
                              style={{ alignSelf: "center", cursor: "pointer" }}
                              onClick={() => setEditPersonRelationship(item)}
                            />
                            <Icon
                              name="trash"
                              style={{ alignSelf: "center", cursor: "pointer" }}
                              onClick={() => {
                                setIdPersonRelationship(item.idPersonRelationship);
                                setDeleteModalOpen(true);
                                setAddPersonRelationship(false);
                                resetForm();
                              }}
                            />
                          </Label>
                        </TableCollum>
                      </TableRow>
                    ))
                    }
                  />
                </TableBody>
              </Table>
          }


          {!addPersonRelationship && <Row>
            <Col lg={6} md={6} sm={6} xs={12}></Col>
            <Col lg={6} md={6} sm={6} xs={12}>
              <ViewText style={{ marginTop: '15px' }}>
                <Icon name="plus" onClick={() => { setAddNewMarket(true); setAddPersonRelationship(false) }} style={{ cursor: 'pointer' }} />
              </ViewText>
            </Col>
          </Row>}

          {addPersonRelationship && <Form>
            <div style={{ marginTop: 40, marginBottom: 10 }}>
              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="personTailLastName"
                    label="Insurer"
                    component={SearchField}
                    disabled={idPersonRelationship}
                    searchList={searchList}
                    isRequired
                    value={values.personTailLastName && values.personTailLastName}
                    onChange={debouncedChangeHandler}
                    onFocus={() => {
                      setSearchList([]);
                      searchCompanies("");
                    }}
                    canGenerate={false}
                    onSearchSelect={(selectedValue) => {
                      setFieldValue(`personTailLastName`, selectedValue.lastName);
                      setFieldValue(`idPersonTail`, selectedValue.idPerson);
                      setSearchList([]);
                    }}
                  />
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Label>* NDA</Label>
                  <RadioContainer>
                    <Field
                      style={{ marginRight: 40 }}
                      label={'Yes'}
                      name={'ndaStatus'}
                      value={values.ndaStatus}
                      onChange={(e) => { setFieldValue('ndaStatus', e) }}
                      option={true}
                      component={RadioButton}
                    />
                    <Field
                      label={'No'}
                      name={'ndaStatus'}
                      value={values.nda}
                      onChange={(e) => setFieldValue(`ndaStatus`, e,)}
                      option={false}
                      component={RadioButton}
                    />
                  </RadioContainer>
                </Col>
              </Row>

              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    onInputRef={(ref) => inputRef.current = ref}
                    name="capacityExposure"
                    label="Capacity Exposure"
                    isRequired
                    mask={maskHelper.getMask("currency", authContext.region, 2, null, null, 0)}
                    component={InputField}
                  />
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    label="NDA Expiration Date"
                    name="ndaExpireDate"
                    component={InputField}
                    rightIcon='calendar'
                    rightIconClick={() => { setShowCalendar(!showCalendar) }}
                    validation={false}
                    type="text"
                    readOnly
                  />

                  {showCalendar &&
                    <CalendarContainer>
                      <div className="YearNavigation">
                        <DayPicker
                          style
                          fixedWeeks
                          captionLayout="dropdown-buttons"
                          fromYear={1900}
                          toYear={2100}
                          initialMonth={month}
                          fromMonth={fromMonth}
                          toMonth={toMonth}
                          onDayClick={(date) => {
                            const selectedDate = new Date(date) ? new Date(date).toLocaleDateString() : null;
                            setFieldValue('ndaExpireDate', selectedDate);
                            setShowCalendar(false);
                          }}
                          captionElement={({ date, localeUtils }) => (
                            <YearMonthForm
                              date={date}
                              localeUtils={localeUtils}
                              onChange={handleYearMonthChange}
                            />
                          )}
                        />
                      </div>
                    </CalendarContainer>
                  }
                </Col>
              </Row>


              <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    third
                    onClick={() => {
                      setAddNewMarket(false)
                      setIdPersonRelationship(null);
                      setAddPersonRelationship(false);
                      resetForm();
                    }}
                  >
                    CANCEL
                  </Button>
                </ButtonContent>
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    type="submit"
                    primary
                    onClick={() => {
                      createValidation().validate(values).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                      })
                    }}
                    id="btnSubmit">
                    {idPersonRelationship ? "SAVE" : "ADD"}
                  </Button>
                </ButtonContent>
              </ButtonContainer>
            </div>
          </Form>}
        </>
      )}
    </Formik>
  )
}

export default RelationshipInformation;